/**
 * Helper de résolution des placeholders bracket du Mondial 2026.
 *
 * RÈGLE D'OR (validée user 26/02/2026) :
 *   ✅ On ne résout QUE les placeholders 100 % sûrs : 1er et 2ème de groupe.
 *   ❌ Les 3èmes (best-thirds) ne sont JAMAIS résolus — le slot dépend du
 *      classement FIFA des meilleurs 3èmes (4 sur 12) ET de l'assignation
 *      officielle du bracket. Tant que ce n'est pas tranché, on garde le
 *      placeholder pour ne PAS afficher une fausse équipe.
 *
 * FORMATS RECONNUS :
 *   - Court "1A", "2B", "3C/E/F/H/I"           → format Viagogo EN
 *   - Long FR "1er Groupe A", "2e Groupe B",   → format Viagogo FR
 *             "2ème Groupe B", "3e C/E/F/H/I",
 *             "3ème C/E/F/H/I"
 *
 * Exemples :
 *   "1A vs 3C/E/F/H/I - Round of 32 (Match 79)"           → "Mexique vs 3ème C/E/F/H/I - Round of 32 (Match 79)"
 *   "( 1/16E ) - 1er Groupe A - 3e C/E/F/H/I (Match 79)"  → "( 1/16E ) - Mexique - 3ème C/E/F/H/I (Match 79)"
 *   "2K vs 2L - Round of 32 (Match 83)"                    → "Argentine vs Portugal" (si présents dans le mapping)
 *
 * Conservatif :
 *   - Si `resolved` est vide → texte original inchangé
 *   - Si l'équipe n'est pas dans le mapping → placeholder gardé tel quel
 */

// Mapping rank → préfixe FR ordinal (utilisé en sortie quand on reformate les compounds)
const RANK_FR = { 1: '1er', 2: '2e', 3: '3ème', 4: '4e' };

// ─── 🆕 Mapping HARDCODÉ du bracket FIFA WC 2026 (M73 → M104) ──────────────
// Source : schéma FIFA officiel (image tableau final utilisateur 28/06/2026).
// Utilisé quand le texte contient "(Match XX)" : on écrase TOUS les placeholders.
// - R32 (M73-M88) : équipes CONFIRMÉES (groupes terminés).
// - R16+ : sources hardcodées → on calcule dynamiquement (vainqueur détecté OU
//   paire d'équipes possibles) à partir du _matchLookupCache API.
const WC26_R32_TEAMS = {
  73: { t1: 'Afrique du Sud', t2: 'Canada' },
  74: { t1: 'Allemagne', t2: 'Paraguay' },
  75: { t1: 'Pays-Bas', t2: 'Maroc' },
  76: { t1: 'Brésil', t2: 'Japon' },
  77: { t1: 'France', t2: 'Suède' },
  78: { t1: "Côte d'Ivoire", t2: 'Norvège' },
  79: { t1: 'Mexique', t2: 'Équateur' },
  80: { t1: 'Angleterre', t2: 'RD Congo' },
  81: { t1: 'États-Unis', t2: 'Bosnie-Herzégovine' },
  82: { t1: 'Belgique', t2: 'Sénégal' },
  83: { t1: 'Portugal', t2: 'Croatie' },
  84: { t1: 'Espagne', t2: 'Autriche' },
  85: { t1: 'Suisse', t2: 'Algérie' },
  86: { t1: 'Argentine', t2: 'Cap-Vert' },
  87: { t1: 'Colombie', t2: 'Ghana' },
  88: { t1: 'Australie', t2: 'Égypte' },
};
// Sources des matchs à partir du R16 : "M89 = vainqueur M74 vs vainqueur M77"
const WC26_BRACKET_SOURCES = {
  89: [74, 77], 90: [73, 75], 91: [76, 78], 92: [79, 80],
  93: [83, 84], 94: [81, 82], 95: [86, 88], 96: [85, 87],
  97: [89, 90], 98: [93, 94], 99: [91, 92], 100: [95, 96],
  101: [97, 98], 102: [99, 100],
  104: [101, 102],
};

/**
 * Calcule les vainqueurs de tous les matchs terminés à partir du _matchLookupCache.
 * Utilise home_winner / away_winner (flags officiels api-football, gère TAB).
 * Retourne { 73: 'Canada', 74: 'Paraguay', ... }
 */
const computeAutoWinners = () => {
  const winners = {};
  const allFixtures = Object.values(_matchLookupCache || {}).flat();
  if (allFixtures.length === 0) return winners;

  const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const sameTeams = (fx, ta, tb) => {
    const fh = norm(fx.home), fa = norm(fx.away), na = norm(ta), nb = norm(tb);
    const m1 = (fh === na || fh.includes(na) || na.includes(fh)) && (fa === nb || fa.includes(nb) || nb.includes(fa));
    const m2 = (fh === nb || fh.includes(nb) || nb.includes(fh)) && (fa === na || fa.includes(na) || na.includes(fa));
    return m1 || m2;
  };
  const winnerOf = (fx) => {
    if (!fx.is_finished) return null;
    if (fx.home_winner === true) return fx.home;
    if (fx.away_winner === true) return fx.away;
    const hg = fx.home_goals, ag = fx.away_goals;
    if (typeof hg === 'number' && typeof ag === 'number') {
      if (hg > ag) return fx.home;
      if (ag > hg) return fx.away;
    }
    return null;
  };

  // R32 : équipes hardcodées → chercher fixture matching
  Object.entries(WC26_R32_TEAMS).forEach(([num, m]) => {
    const fx = allFixtures.find(f => sameTeams(f, m.t1, m.t2));
    if (fx) { const w = winnerOf(fx); if (w) winners[num] = w; }
  });
  // R16 → SF → Finale : cascade
  const cascade = [
    [89, 90, 91, 92, 93, 94, 95, 96],
    [97, 98, 99, 100],
    [101, 102],
    [104],
  ];
  cascade.forEach(round => {
    round.forEach(num => {
      const src = WC26_BRACKET_SOURCES[num];
      if (!src) return;
      const w1 = winners[src[0]], w2 = winners[src[1]];
      if (!w1 || !w2) return;
      const fx = allFixtures.find(f => sameTeams(f, w1, w2));
      if (fx) { const w = winnerOf(fx); if (w) winners[num] = w; }
    });
  });
  return winners;
};

/**
 * Calcule le "side" (équipe ou paire) d'un match par récursion.
 * - Si vainqueur connu → nom de l'équipe.
 * - Si R32 → nom des 2 équipes séparés par "/".
 * - Sinon → paires imbriquées : (T1/T2)/(T3/T4).
 */
const computeMatchSide = (matchNum, winners) => {
  if (winners[matchNum]) return winners[matchNum];
  const r32 = WC26_R32_TEAMS[matchNum];
  if (r32) return `${r32.t1}/${r32.t2}`;
  const src = WC26_BRACKET_SOURCES[matchNum];
  if (src) return `${computeMatchSide(src[0], winners)}/${computeMatchSide(src[1], winners)}`;
  return `?`;
};

/**
 * Récupère les équipes d'un match par son numéro WC2026 (73-104).
 * Retourne { t1, t2 } dynamique (utilise l'API pour les rounds avancés).
 */
export const getWC26BracketTeams = (matchNum) => {
  if (!matchNum || typeof matchNum !== 'number') return null;
  const r32 = WC26_R32_TEAMS[matchNum];
  if (r32) return { t1: r32.t1, t2: r32.t2 };
  const src = WC26_BRACKET_SOURCES[matchNum];
  if (!src) return null;
  const winners = computeAutoWinners();
  return { t1: computeMatchSide(src[0], winners), t2: computeMatchSide(src[1], winners) };
};

/**
 * Reconstruit le préfixe "TEAM1 vs TEAM2" en utilisant le bracket.
 * Pour R32 : équipes exactes. Pour R16+ : vainqueur si connu, sinon paire.
 */
const resolveByMatchNumber = (eventText) => {
  if (!eventText || typeof eventText !== 'string') return null;
  const m = eventText.match(/\(Match\s+(\d+)\)/i);
  if (!m) return null;
  const num = parseInt(m[1], 10);
  return getWC26BracketTeams(num);
};

export const resolveEventName = (eventText, resolved) => {
  if (!eventText || typeof eventText !== 'string') return eventText;

  // ─── 0a) NOUVEAU : Remplacement des patterns W(\d+) (Winner of match NN) ─
  // Viagogo utilise "W74 VS W77" pour les 1/8+ tant que les vainqueurs ne sont
  // pas déterminés. Dès qu'un match R32+ est terminé, on remplace WNN par le
  // vainqueur détecté (via api-football). Sinon on affiche la paire d'équipes.
  // Exemple : "( ROUND OF 16 ) - W74 VS W77 . WC"
  //   → M74 fini (Paraguay vainqueur) : "( ROUND OF 16 ) - Paraguay VS France/Suède . WC"
  //   → M74 non fini : "( ROUND OF 16 ) - Allemagne/Paraguay VS France/Suède . WC"
  if (/W\d{2,3}/i.test(eventText)) {
    const winners = computeAutoWinners();
    eventText = eventText.replace(/\bW(\d{2,3})\b/gi, (match, num) => {
      const n = parseInt(num, 10);
      // Vainqueur connu → nom d'équipe direct
      if (winners[n]) return winners[n];
      // Match R32 → paire d'équipes
      const r32 = WC26_R32_TEAMS[n];
      if (r32) return `${r32.t1}/${r32.t2}`;
      // Match R16+ → paire d'équipes calculée récursivement
      if (WC26_BRACKET_SOURCES[n]) return computeMatchSide(n, winners);
      // Numéro inconnu → on laisse le placeholder original
      return match;
    });
  }

  // ─── 0b) PRIORITÉ ABSOLUE : résolution par numéro de match WC2026 ─────
  // Si le texte contient "(Match XX)" et qu'on connaît les équipes via le
  // bracket FIFA hardcodé, on écrase TOUT le préfixe par les vraies équipes.
  // Couvre les 2 formats Viagogo :
  //   EN : "1A vs 3C/E/F/H/I - Round of 32 (Match 79) [id]"
  //   FR : "( 1/16E ) - 1er Groupe A - 3e C/E/F/H/I (Match 79)"
  const byNum = resolveByMatchNumber(eventText);
  if (byNum) {
    // Format EN : "X vs Y - Round/Quarter/Semi/Final ... (Match XX)"
    const enSuffix = eventText.match(/(\s*-\s*(?:Round of \d+|Quarter[-\s]?finals?|Semi[-\s]?finals?|Final|Group Stage)\s*\(Match\s+\d+\).*)$/i);
    if (enSuffix) {
      return `${byNum.t1} vs ${byNum.t2}${enSuffix[1]}`;
    }
    // Format FR : "( 1/16E ) - X - Y (Match XX)"
    const frPrefix = eventText.match(/^(\s*\(\s*\d+\/\d+E?\s*\)\s*-\s*)/i);
    if (frPrefix) {
      const matchTail = eventText.match(/(\(Match\s+\d+\).*)$/i);
      const tail = matchTail ? matchTail[1] : '';
      return `${frPrefix[1]}${byNum.t1} - ${byNum.t2} ${tail}`.trim();
    }
    // Format générique sans suffixe reconnu : remplace juste le début "X vs Y"
    return eventText.replace(/^[^()]+?(?=\s*(?:-|\())/, `${byNum.t1} vs ${byNum.t2} `);
  }

  if (!resolved || Object.keys(resolved).length === 0) return eventText;

  let text = eventText;

  // ─── 1) NORMALISATION du format long FR → format court interne ─────────
  // "1er Groupe A"      → "1A"
  // "2e Groupe B"       → "2B"
  // "2ème Groupe B"     → "2B"
  // "3ème Groupe C"     → "3C"
  // (insensible à la casse, accepte "ere/ère/eme/ème/e")
  text = text.replace(
    /\b([1-4])(?:er|ère|ere|ème|eme|e)\s+Groupe\s+([A-L])\b/gi,
    (_m, rank, letter) => `${rank}${letter.toUpperCase()}`
  );

  // "3e C/E/F/H/I"      → "3C/E/F/H/I"   (puis géré par l'étape 2 ci-dessous)
  // "3ème C/E/F/H/I"    → "3C/E/F/H/I"
  text = text.replace(
    /\b([1-4])(?:er|ère|ere|ème|eme|e)\s+([A-L](?:\/[A-L])+)\b/gi,
    (_m, rank, group) => `${rank}${group.toUpperCase()}`
  );

  // ─── 2) Pretty-print des compound 3rd-place placeholders ──────────────
  // "3C/E/F/H/I" → "3ème C/E/F/H/I"   (pas de résolution — incertain)
  // Idem 4X/Y/Z (sécurité, ne devrait pas apparaître en knockout).
  text = text.replace(
    /\b([1-4])([A-L])((?:\/[A-L])+)\b/g,
    (_m, rank, firstLetter, rest) => `${RANK_FR[rank] || rank} ${firstLetter}${rest}`
  );

  // ─── 3) Résolution des placeholders SÛRS uniquement (1X et 2X) ────────
  // Lookaround : pas précédé/suivi de "/" ou alphanum (pour éviter compounds
  // déjà réécrits "1er A/B" et autres faux positifs).
  text = text.replace(
    /(?<![A-Za-z0-9/])([12])([A-L])(?![A-La-z0-9/])/g,
    (match, rank, letter) => {
      const key = `${rank}${letter}`;
      return resolved[key] || match;
    }
  );

  return text;
};

/**
 * Cache mémoire global. Set par App.js après login (et toutes les 5 min).
 * Permet à des composants profonds d'appeler resolveEventNameGlobal()
 * sans devoir trimballer le mapping en prop drilling.
 */
let _resolvedCache = {};

export const setResolvedTeams = (resolved) => {
  _resolvedCache = resolved || {};
};

export const getResolvedTeams = () => _resolvedCache;

export const resolveEventNameGlobal = (eventText) => resolveEventName(eventText, _resolvedCache);

// ─── 🆕 Croisement avec les fixtures api-football (plus fiable que la résolution placeholder) ─────
// Cache : { "2026-06-28": [{home, away, venue, round, confident}, ...] }
let _matchLookupCache = {};

export const setMatchLookup = (byDate) => {
  _matchLookupCache = byDate || {};
};

export const getMatchLookup = () => _matchLookupCache;

/**
 * Tente de résoudre un événement via la fixture officielle api-football pour cette date.
 * Retourne { text, source, doute, reason } :
 *   - source='api' : les vraies équipes ont été trouvées dans api-football (match exact via date + heure + stade)
 *   - source='placeholder' : fallback sur résolution placeholder (notre regex)
 *   - source='doute' : plusieurs fixtures match OU contradiction → on marque DOUTE
 *
 * @param {string} eventText - libellé brut du ticket (peut contenir "1A vs 3C/E/F/H/I")
 * @param {string} dateEvenement - date au format ISO "YYYY-MM-DD" ou "DD/MM/YYYY HH:MM"
 * @param {object} [extras] - { lieu, ville } pour désambiguïsation stade/ville
 * @returns {{text: string, source: 'api'|'placeholder'|'doute', doute: boolean, reason?: string}}
 */
export const resolveEventByApi = (eventText, dateEvenement, extras) => {
  const placeholderResult = resolveEventNameGlobal(eventText);

  if (!dateEvenement || typeof dateEvenement !== 'string') {
    return { text: placeholderResult, source: 'placeholder', doute: false };
  }

  // Normalisation date : extrait YYYY-MM-DD + HH:MM si présent.
  // Formats acceptés :
  //   "2026-06-30T19:00:00" / "2026-06-30 19:00" / "30/06/2026 19:00"
  //   "mar. 30/06/2026 19:00" / "30/06/2026" / "2026-06-30"
  const rawDate = dateEvenement.trim();
  let dateIso = '';
  let ticketHour = null; // 0-23, null si inconnu
  const ddmm = rawDate.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[\sT]+(\d{1,2})[:h](\d{2}))?/);
  const yyyymmdd = rawDate.match(/(\d{4})-(\d{1,2})-(\d{1,2})(?:[\sT]+(\d{1,2}):(\d{2}))?/);
  if (ddmm) {
    dateIso = `${ddmm[3]}-${ddmm[2].padStart(2, '0')}-${ddmm[1].padStart(2, '0')}`;
    if (ddmm[4]) ticketHour = parseInt(ddmm[4], 10);
  } else if (yyyymmdd) {
    dateIso = `${yyyymmdd[1]}-${yyyymmdd[2].padStart(2, '0')}-${yyyymmdd[3].padStart(2, '0')}`;
    if (yyyymmdd[4]) ticketHour = parseInt(yyyymmdd[4], 10);
  } else {
    return { text: placeholderResult, source: 'placeholder', doute: false };
  }

  // Récupère les fixtures pour CETTE date (locale ticket).
  // 🌐 Cross-midnight UTC : si matchStartUTC est fourni (timestamp ms du calendrier FIFA),
  // on ajoute AUSSI les fixtures de la date UTC du kickoff. Ex: M81 Santa Clara 17h Pacific
  // = 02/07 00:00 UTC mais date ticket = 01/07 → on doit chercher dans les 2 clés.
  const _kickoffUTC = extras?.matchStartUTC;
  const kickoffDateIso = (typeof _kickoffUTC === 'number' && _kickoffUTC > 0)
    ? new Date(_kickoffUTC).toISOString().slice(0, 10)
    : null;
  const _pool1 = _matchLookupCache[dateIso] || [];
  const _pool2 = (kickoffDateIso && kickoffDateIso !== dateIso) ? (_matchLookupCache[kickoffDateIso] || []) : [];
  // Déduplication par fixture_id (au cas où la fixture serait dans les 2 pools)
  const _seen = new Set();
  const fixtures = [..._pool1, ..._pool2].filter(f => {
    const id = f.fixture_id;
    if (id && _seen.has(id)) return false;
    if (id) _seen.add(id);
    return true;
  });
  if (fixtures.length === 0) {
    return { text: placeholderResult, source: 'placeholder', doute: false };
  }

  // Round filter (Round of 32, Quart, Demi, etc.)
  const lower = eventText.toLowerCase();
  const matchesRound = (round) => {
    const r = (round || '').toLowerCase();
    if (r === 'round of 32') return /round of 32|1\/16/.test(lower);
    if (r === 'round of 16') return /round of 16|1\/8/.test(lower);
    if (r === 'quarter-finals') return /quarter|1\/4|quart/.test(lower);
    if (r === 'semi-finals') return /semi|1\/2|demi/.test(lower);
    if (r === 'final') return /final/.test(lower) && !/semi|3rd/.test(lower);
    if (r === '3rd place final') return /3rd place|petite finale/.test(lower);
    if (r.startsWith('group stage')) return /group|groupe/.test(lower);
    return false; // 🔒 strict : on n'accepte JAMAIS une fixture dont le round n'est pas reconnu
  };
  const roundFiltered = fixtures.filter(f => matchesRound(f.round || ''));

  // 🛡️ STRICT : si AUCUNE fixture ne match le round du ticket → fallback placeholder.
  // Plus de "pool unfiltered" qui faisait que les matchs de Group Stage prenaient
  // les équipes d'un Round of 32 sur la même date (iteration_65 bug).
  if (roundFiltered.length === 0) {
    return { text: placeholderResult, source: 'placeholder', doute: false };
  }
  const pool = roundFiltered;

  // 🎯 SCORING précis pour désambiguïser quand plusieurs fixtures partagent la date.
  // Critères :
  //   +60 si on a `matchStartUTC` (timestamp précis du coup d'envoi venant de wc2026Calendar)
  //        et qu'il matche à ±30 min près le timestamp de la fixture API
  //   +50 si l'heure du ticket (parsée de date_evenement) est à ±1.5h de la fixture API
  //   +30 si le stade correspond (fuzzy match)
  //   +20 si la ville correspond
  // Un match est jugé "fiable" uniquement si score >= 30 ET strictement > 2ème meilleur.
  const _safeStr = (v) => {
    if (typeof v === 'string') return v;
    if (v && typeof v === 'object' && typeof v.name === 'string') return v.name;
    return '';
  };
  const ticketLieu = _safeStr(extras?.lieu).toLowerCase();
  const ticketVille = _safeStr(extras?.ville).toLowerCase();
  const matchStartUTC = (typeof extras?.matchStartUTC === 'number' && extras.matchStartUTC > 0) ? extras.matchStartUTC : null;

  const _fuzzyContains = (a, b) => {
    if (!a || !b) return false;
    const na = a.replace(/[^a-z0-9]/g, '');
    const nb = b.replace(/[^a-z0-9]/g, '');
    if (na.length < 3 || nb.length < 3) return false;
    return na.includes(nb) || nb.includes(na);
  };

  const scored = pool.map(f => {
    let score = 0;
    // Match UTC timestamp précis (priorité forte — calendrier hardcodé FIFA)
    if (matchStartUTC && f.timestamp) {
      const diffMin = Math.abs((f.timestamp * 1000) - matchStartUTC) / 60000;
      if (diffMin <= 30) score += 60;
    }
    // Heure (en UTC dans l'API). Comparaison en heure UTC du ticket aussi.
    // Tolérance ±1.5h pour distinguer 2 matchs sur la même date (souvent espacés de 3h).
    if (ticketHour !== null && f.date) {
      const fixDate = new Date(f.date);
      const fixHour = fixDate.getUTCHours() + fixDate.getUTCMinutes() / 60;
      // Convertit l'heure locale (Europe/Paris ~ UTC+2 en juin) en UTC.
      const ticketUtc = (ticketHour + 24 - 2) % 24;
      const diff = Math.min(Math.abs(fixHour - ticketUtc), 24 - Math.abs(fixHour - ticketUtc));
      if (diff <= 1.5) score += 50;
    }
    // Stade
    if (ticketLieu && (_fuzzyContains(ticketLieu, (f.venue || '').toLowerCase()) || _fuzzyContains(ticketLieu, (f.city || '').toLowerCase()))) {
      score += 30;
    }
    // Ville
    if (ticketVille && _fuzzyContains(ticketVille, (f.city || '').toLowerCase())) {
      score += 20;
    }
    return { f, score };
  }).sort((a, b) => b.score - a.score);

  const best = scored[0];
  const second = scored[1];

  // Match fiable : meilleur score >= 30 ET nettement supérieur au 2ème (sinon ambiguïté)
  if (best && best.score >= 30 && (!second || best.score - second.score >= 20)) {
    return {
      text: `${best.f.home} vs ${best.f.away}`,
      source: 'api',
      doute: false,
    };
  }

  // 1 seule fixture dans le pool ET aucune info contraignante fournie ET
  // le round du texte matche déjà la fixture (sécurité supplémentaire vs régression group stage)
  if (pool.length === 1 && !ticketLieu && ticketHour === null) {
    const f = pool[0];
    return { text: `${f.home} vs ${f.away}`, source: 'api', doute: false };
  }

  // Sinon : ambigu → DOUTE
  if (pool.length >= 2) {
    return {
      text: placeholderResult,
      source: 'doute',
      doute: true,
      reason: `${pool.length} fixtures api-football le ${dateIso} et impossible de désambiguïser via heure/stade${ticketHour !== null ? ` (ticket à ${ticketHour}h)` : ''}${ticketLieu ? ` (stade: ${ticketLieu})` : ''}`,
      candidates: pool.map(c => `${c.home} vs ${c.away} (${(c.venue || '').slice(0, 30)})`),
    };
  }

  // 1 fixture mais score 0 (heure/stade différents) → ne PAS utiliser, fallback placeholder
  return { text: placeholderResult, source: 'placeholder', doute: false };
};

